import React, { useMemo, useState } from "react";
import { usePage } from "@inertiajs/inertia-react";
import { LabeledInput, LabeledSelect } from "@/Components/Form";
import { useAtom, useAtomValue } from "jotai";
import { focusAtom } from "jotai-optics";
import { splitAtom } from "jotai/utils";
import { MoreVertical, Trash } from "react-feather";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { uuid } from "@/Utilities/Utilities";

function SortableItem({
  item,
  i,
  emissionEnabled,
  errorBag,
  onRemove = () => null,
}) {
  const readItem = useAtomValue(item);
  return (
    <InputTypeItem
      id={readItem.uuid}
      sortIndex={i}
      emissionEnabled={emissionEnabled}
      errorBag={errorBag}
      item={item}
      onRemove={onRemove}
    />
  );
}

function InputTypeItem({
  item,
  sortIndex,
  errorBag,
  id,
  onRemove = () => null,
}) {
  const [payload, setPayload] = useAtom(item);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const errors = usePage().props?.errors || {};

  const handleFormUpdate = (e) => {
    const { name, value } = e.target;
    setPayload((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="flex w-full items-center" ref={setNodeRef} style={style}>
      <button className="mt-2 flex cursor-grab" {...attributes} {...listeners}>
        <MoreVertical className="-ml-2" stroke="#1F4444" />
        <MoreVertical className="-ml-4" stroke="#1F4444" />
      </button>
      <LabeledInput
        name="label"
        title={`Alternative ${sortIndex + 1}*`}
        placeholder={`Alternative ${sortIndex + 1}*`}
        className="mb-4"
        value={payload?.label || ""}
        onChange={handleFormUpdate}
        containerClass="grow select-none"
        error={
          errors?.[errorBag] &&
          errors?.[errorBag]?.[`inputs.${sortIndex}.label`]
        }
      />
      <span className="hover-circle relative ml-4 mt-2">
        <Trash className="cursor-pointer" onClick={onRemove} />
      </span>
    </div>
  );
}

export function InputTypeDistributeForm({ atom }) {
  const errors = usePage().props?.errors || {};

  const inputsAtom = useMemo(
    () => focusAtom(atom, (optic) => optic.prop("inputs")),
    [atom],
  );
  const inputsAtomsAtom = useMemo(() => splitAtom(inputsAtom), [atom]);

  const [basePayload, setBasePayload] = useAtom(atom);
  const [inputArrayAtoms, setInputsArray] = useAtom(inputsAtom);
  const inputsAtoms = useAtomValue(inputsAtomsAtom);

  const [unitType, setUnitType] = useState(
    inputArrayAtoms?.[0]?.unit ? "number" : "",
  );

  const errorBag = basePayload.errorBag;

  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over) return;

    if (active.id !== over.id) {
      setInputsArray((items) => {
        const oldIndex = items.findIndex(({ uuid }) => uuid === active.id);
        const newIndex = items.findIndex(({ uuid }) => uuid === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  const handleAddInput = () => {
    setInputsArray((prev) => [
      ...prev,
      { label: "", unit: inputArrayAtoms?.[0]?.unit || null, uuid: uuid() },
    ]);
  };

  const handleRemoveInput = (uuid) => {
    setInputsArray((prev) => prev.filter((i) => i.uuid !== uuid));
  };

  const handleChangeUnitType = (e) => {
    const { value } = e.target;

    setUnitType(value);

    if (value === "") {
      handleFormUpdateAllItems({ target: { name: "unit", value: null } });
    }
  };

  const sensors = useSensors(useSensor(PointerSensor));

  // This will update all inputs when making changes
  const handleFormUpdateAllItems = (e) => {
    const { name, value } = e.target;
    setInputsArray((prev) => prev.map((i) => ({ ...i, [name]: value })));
  };

  return (
    <div className="flex w-full flex-col items-start">
      {!basePayload?.id && (
        <p className="mb-4">
          Distribute allows you to distribute impact of two or more different
          entities. You can choose between a percentage or a number with a
          custom unit.
        </p>
      )}

      <div className="w-full">
        <LabeledInput
          name="question_text"
          title="What's your question?*"
          placeholder="Describe what you want the reporter to distribute"
          className="mb-4"
          value={basePayload?.question_text || ""}
          onChange={(e) =>
            setBasePayload((prev) => ({
              ...prev,
              [e.target.name]: e.target.value,
            }))
          }
          error={errors?.[errorBag] && errors?.[errorBag]?.question_text}
        />
      </div>

      <div className="w-full">
        <DndContext
          sensors={sensors}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={inputArrayAtoms.map((item) => item.uuid)}
            strategy={verticalListSortingStrategy}
          >
            {inputsAtoms.map((item, i) => (
              <SortableItem
                key={inputArrayAtoms[i].uuid}
                item={item}
                i={i}
                errorBag={errorBag}
                onRemove={() => handleRemoveInput(inputArrayAtoms[i].uuid)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <button
        className="text-blue underline hover:no-underline"
        onClick={handleAddInput}
      >
        Add more alternatives
      </button>

      <div className="mt-4 flex w-full gap-4 max-md:flex-wrap">
        <LabeledSelect
          title="Choose what kind of input you want"
          options={[{ label: "Number", value: "number" }]}
          containerClass="grow"
          onChange={handleChangeUnitType}
          defaultOption="Percentage"
          defaultValue={unitType}
        />
        <LabeledInput
          name="unit"
          title="Unit*"
          disabled={unitType !== "number"}
          placeholder="Name your unit"
          containerClass={`grow ${unitType !== "number" ? "opacity-50" : ""}`}
          onChange={handleFormUpdateAllItems}
          value={inputArrayAtoms?.[0]?.unit || ""}
          error={errors?.[errorBag] && errors?.[errorBag]?.[`inputs.0.unit`]}
        />
      </div>
    </div>
  );
}
